import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useClinicalStudyAttention } from './use-clinical-study-attention';

/**
 * What a module's navigation badge shows, and what it is called.
 *
 * `label` is a full sentence rather than a number, because the number alone is not announced: the
 * rail entry carries an `aria-label` (it has to — the compressed rail hides the visible label), and
 * an `aria-label` REPLACES the element's contents in the accessibility tree, badge included. A
 * screen-reader user would hear "Studi clinici" and never learn there were three visits at risk.
 * The label is appended to that name, so the badge is legible both ways.
 */
export type AttentionCount = {
  readonly value: number;
  readonly label: string;
};

/** Module id → its badge. A module with nothing to report has no entry at all. */
export type AttentionCounts = Readonly<Record<string, AttentionCount>>;

/**
 * THE registry of navigation counters — one hook call per contributing module, and today exactly
 * one module contributes.
 *
 * ADDING A MODULE'S COUNTER IS TWO LINES: its own `use<Module>Attention` hook call here, and its
 * entry in the map below. Every hook is called unconditionally and in a fixed order, which is what
 * keeps this a legal hook body while still reading like a registry; a module whose read is
 * unavailable returns `undefined` and contributes nothing.
 *
 * A ZERO IS NOT A BADGE. `> 0` is the gate: "nothing needs your attention" is exactly what an
 * absent badge already says, and a rail decorated with grey zeroes trains people to stop reading
 * the numbers that do matter. This is also why the count is a plain number here rather than a
 * three-state — `undefined` (not known) and `0` (known to be nothing) are different facts on the
 * wire, and they collapse to the same rendering: nothing.
 */
export function useAttentionCounts(organizationId: string | undefined): AttentionCounts {
  const { t } = useTranslation('shell');
  const clinicalStudy = useClinicalStudyAttention(organizationId);

  return useMemo(() => {
    const counts: Record<string, AttentionCount> = {};

    if (clinicalStudy !== undefined && clinicalStudy > 0) {
      counts['clinical-study'] = {
        value: clinicalStudy,
        label: t('nav.counters.clinicalStudy', { count: clinicalStudy }),
      };
    }

    return counts;
  }, [clinicalStudy, t]);
}
